/**
 * The confirmed Fuel Stop menu, taken verbatim from the boards either side
 * of the serving hatch — the photograph in `IMAGES.fuelStopHatch` is the
 * source, so every price here is readable evidence rather than an
 * invention.
 *
 * IMPORTANT CONTEXT, confirmed by the client 2026-09-12: this is the
 * **catering truck in the car park**, and it is currently the only source
 * of hot food. The café's own kitchen is not fitted yet. Inside, the
 * counter does the lighter end — coffee, cakes, snacks, ice cream.
 *
 * The truck is out **every day the café is open** — it is part of the
 * business rather than a bike-night extra — so this menu is always
 * available during opening hours and should be presented as such. What
 * the page must still not do is imply a full indoor kitchen service that
 * does not exist yet. See `HOT_FOOD_NOTE`. Registry D43.
 *
 * Prices are strings rather than typed values on purpose: these are the
 * printed prices as written, and nothing computes a total from them. The
 * moment something does — an order, a basket — they become numbers.
 */
export interface MenuItem {
  name: string
  /**
   * Omitted where the board does not print one. The ice cream counter
   * prices by scoop and tub on its own display, so inventing a figure here
   * would be worse than sending people to look.
   */
  price?: string
  description?: string
  /** Renders as "Choose from: a · b · c". */
  choices?: string[]
}

export interface MenuSection {
  heading: string
  /** Sits beside the heading, e.g. "Served all day". */
  supportingText?: string
  items: MenuItem[]
  /** Applies to the whole section, so it sits below the list. */
  footnote?: string
}

export const menuSections: MenuSection[] = [
  {
    heading: 'Breakfast Baps',
    supportingText: 'Served all day',
    items: [
      {
        name: 'Cured Bacon Bap',
        price: '£6.50',
        description: 'Howarths of Belper large bap with a free-range egg.',
      },
      {
        name: 'Pork Sausage Bap',
        price: '£6.50',
        description: 'Howarths of Belper large bap with a free-range egg.',
      },
    ],
  },
  {
    heading: 'Low On Fuel Pies',
    items: [
      {
        name: 'Handmade Savoury Pie',
        price: '£8.00',
        choices: ['Steak & Ale', 'Cheese & Onion', 'Chicken, Ham & White Wine'],
      },
    ],
    footnote: 'All our pies come with mushy peas and mint sauce.',
  },
  {
    heading: 'Get Smashed!',
    items: [
      {
        name: 'Low On Fuel Smashed Burger',
        price: '£9.00',
        description:
          'Two 100% beef patties with our own secret seasoning, topped with melted cheese and homemade relish.',
      },
    ],
  },
]

/**
 * The hot drinks board, transcribed from the glass panel at the coffee bar.
 *
 * The prices were previously absent because they were not legible in the
 * first photograph of the board. `IMAGES.drinksBoardPrices` is a square-on
 * shot in which every dotted leader reads clearly, so they are now given
 * exactly as printed. Registry D34.
 */
export const DRINKS = {
  heading: 'The coffee bar',
  supportingText: 'Inside, at the counter',
  items: [
    { name: 'Latte', price: '£3.80' },
    { name: 'Cappuccino', price: '£3.80' },
    { name: 'Flat white', price: '£3.80' },
    { name: 'Americano', price: '£3.80' },
    { name: 'Espresso', price: '£2.50' },
    { name: 'Choice of teas', price: '£2.50' },
    { name: 'Clipper hot chocolate', price: '£3.50' },
  ],
} as const

/**
 * Cakes. £3.50 is now confirmed twice over: the handwritten sign on the
 * counter, and the chalk sign beside the cabinet in the drinks-board shot.
 * What is actually in the cabinet changes daily, which is why this is a
 * sentence and a single price rather than a priced list.
 */
export const CAKES = {
  heading: 'Cakes',
  body: 'There is a cabinet of cakes on the counter, cut thick and made locally. What is in it depends on the day, so have a look rather than asking us to promise anything.',
  priceNote: 'All cakes £3.50.',
} as const

/**
 * The bar, deliberately stated without a drinks list.
 *
 * The client's instruction 2026-09-12 was not to advertise what is on tap
 * but to get people through the door instead — which is also the honest
 * position, since what is on changes and none of it is on record here.
 * So this says the place is licensed, and stops. Registry D31, D45.
 */
export const BAR_NOTE = {
  heading: 'There is a bar, too',
  body: 'We are a café and a pub both, so there is a proper bar inside alongside the coffee. Come and have a look at what we have on, and we will pour you something.',
} as const

/**
 * The ice cream counter is inside the café rather than at the Fuel Stop
 * truck, so it gets its own block rather than a fourth menu section.
 *
 * £3.50 is confirmed by the client 2026-09-12.
 *
 * THE FLAVOURS AND THE MAKER BELOW ARE PLACEHOLDERS, at the client's
 * request, and are marked as such in the interface rather than only in
 * this comment — a list of flavours is exactly the kind of detail
 * someone rides out for, and a silent placeholder would be read as a
 * promise. Registry D44.
 */
export const ICE_CREAM = {
  heading: 'The ice cream counter',
  body: 'Two cabinets of gelato, scooped to order into a tub or a cone, and dipped in chocolate and hundreds and thousands if that is how you like it.',
  price: '£3.50',
  priceNote: 'Any flavour, tub or cone, £3.50.',
  /**
   * Deliberately NOT a placeholder supplier name. A plausible invented
   * dairy — "Derbyshire Dales Creamery" was the one I had here — is very
   * likely to be an actual business, and naming the wrong producer on a
   * menu misattributes someone's goods. A flagged placeholder flavour is
   * harmless; a flagged placeholder company is not, because a reader who
   * misses the flag has been told something false about a third party.
   * So the maker is simply absent until the real one is supplied.
   */
  supportingText: 'Inside, at the counter',
  /** Placeholder list. The cabinet is the truth until these are confirmed. */
  flavours: [
    'Madagascan Vanilla',
    'Belgian Chocolate',
    'Salted Caramel',
    'Strawberry & Cream',
    'Mint Choc Chip',
    'Honeycomb',
  ],
  /** Rendered on the page. Do not remove while the list above is invented. */
  placeholderNote:
    'Flavours change with what the cabinet has in, so treat this list as a rough idea and have a proper look when you get here.',
} as const

/**
 * Real signage, photographed at the hatch. Worth saying plainly: someone
 * who rides out expecting to pay cash has a wasted journey.
 */
export const PAYMENT_NOTE =
  'The Fuel Stop is card only, so please do not ride out expecting to pay cash.'

/**
 * Where hot food actually comes from, and where it does not yet.
 *
 * This is the single most consequential fact on the Food & Drink page, so
 * it is stated at the top of it rather than as a footnote. The café is
 * mid-way through fitting a kitchen; until that happens the truck is the
 * hot food and the counter is the lighter option. Registry D43.
 */
export const HOT_FOOD_NOTE = {
  heading: 'Where the hot food comes from',
  body: 'All our hot food comes from the Fuel Stop, our catering truck out in the car park. It is there every day we are open, so you will not turn up and find it shut. That is where the baps, the pies and the smashed burger are cooked and served. Inside at the counter you will find the lighter end of things: coffee, cakes, snacks and the ice cream counter.',
  kitchenNote:
    'We are fitting a proper kitchen and a fuller menu will follow it. Until then the truck is the hot food and the counter is everything else.',
} as const

/**
 * Makes no dietary claims. Stating allergens wrongly is materially more
 * dangerous than directing people to ask, so this notice does the latter.
 */
export const MENU_AVAILABILITY_NOTE =
  'What we have on can change from day to day. Do give us a ring if you are coming especially for something, or if you want to ask about ingredients.'
